import { Box, Center, HStack, Image } from "@chakra-ui/react";
import React, { ReactElement, RefCallback, useEffect, useState } from "react";
import { OptionProps, SingleValueProps } from "react-select";

import { Dropdown, IOption } from "adminComponents/atoms/Dropdown";
import { pxToRem } from "adminComponents/utils/pxToRem";
import {
  DEFAULT_HAIR_COLOR_HEX_CODE,
  DEFAULT_SKIN_TONE_COLOR_HEX_CODE,
} from "links/lib/constants";
import { IAvatarItem } from "links/lib/types";
import { getItemPreviewDataURL } from "links/lib/util";

export interface IHairStylePickerProps {
  hairColor?: string;
  skinToneColor?: string;
  hairStyleOptions: Array<IAvatarItem>;
  onHairStyleSelect: (hairStyle: IAvatarItem) => void;
  selectedHairStyle?: IAvatarItem;
}

const HairStylePicker: React.FC<IHairStylePickerProps> = ({
  hairColor = DEFAULT_HAIR_COLOR_HEX_CODE,
  skinToneColor = DEFAULT_SKIN_TONE_COLOR_HEX_CODE,
  hairStyleOptions,
  onHairStyleSelect,
  selectedHairStyle,
}) => {
  const [hairStyleImageElements, setHairStyleImageElements] = useState<{
    [key: string]: ReactElement;
  }>({});

  // Render images for hair styles with provided hair color
  // and skin tone.
  useEffect(() => {
    hairStyleOptions.forEach((hairStyleOption) => {
      (async () => {
        const imageDataUrl = await getItemPreviewDataURL(
          hairStyleOption.preview_image_asset_url,
          "",
          hairColor,
          skinToneColor
        );
        setHairStyleImageElements((curr) => {
          return {
            ...curr,
            [hairStyleOption.id]: (
              <Box w={pxToRem(64)}>
                <Center>
                  <Image
                    maxW={pxToRem(64)}
                    maxH={pxToRem(64)}
                    src={imageDataUrl}
                  />
                </Center>
              </Box>
            ),
          };
        });
      })();
    });
  }, [hairColor, skinToneColor, hairStyleOptions]);

  const options = hairStyleOptions.map((hairStyleOption) => {
    return {
      value: hairStyleOption.id,
      label: hairStyleOption.name,
    };
  });

  const HairStyleOption = (props: OptionProps) => {
    return <HairStyle {...props} />;
  };

  const HairStyleSingleValue = (props: SingleValueProps) => {
    return <HairStyle {...props} />;
  };

  const HairStyle = ({
    innerProps,
    innerRef,
    data,
  }: {
    innerProps: JSX.IntrinsicElements["div"];
    innerRef?: RefCallback<HTMLDivElement>;
    data: unknown;
  }) => {
    const hairStyle = (hairStyleOptions || []).find(
      (o) => o.id === (data as IOption).value
    );
    if (!hairStyle) return <></>;

    const imageElement = hairStyleImageElements[hairStyle.id];
    if (!imageElement) return <></>;

    return (
      <HStack
        ref={innerRef}
        {...innerProps}
        w={innerRef ? "full" : "80%"}
        p={pxToRem(12)}
        __css={{
          _hover: {
            backgroundColor: innerRef ? "primary.light-gray" : undefined,
          },
        }}
      >
        {imageElement}
        <Box>{hairStyle.name}</Box>
      </HStack>
    );
  };

  const handleChangeHairStyle = (hairStyleOption: IOption) => {
    const hairStyle = hairStyleOptions.find(
      (h) => h.id === hairStyleOption.value
    );

    hairStyle && onHairStyleSelect(hairStyle);
  };

  const selectedOption = options.find((o) => o.value === selectedHairStyle?.id);

  return (
    <Dropdown
      options={options}
      handleChange={(o) => handleChangeHairStyle(o as IOption)}
      value={selectedOption}
      components={{
        Option: HairStyleOption,
        SingleValue: HairStyleSingleValue,
      }}
      isSearchable={false}
      isMulti={false}
    ></Dropdown>
  );
};

export { HairStylePicker };
